/**
 * Review-queue bulk assign and complete actions.
 */
import { formatInteger } from "./format.js";
import { authenticatedFetch, DashboardAuthRequiredError, getDashboardOperator } from "./dashboard-auth.js";
import type { DashboardActionHost } from "./dashboard-referenced-deed-actions.js";

const leadFactoryReviewOutcomes = [
  "classification_confirmed",
  "extraction_confirmed",
  "evidence_confirmed",
  "needs_rework",
  "duplicate_reviewed",
  "not_relevant",
  "invalid_pdf",
  "other_typed_review"
] as const;

export function bindReviewQueueBulkActions(statusElement: HTMLElement, actions: DashboardActionHost): void {
  document.querySelectorAll<HTMLButtonElement>("[data-review-bulk-assign]").forEach((button) => {
    button.addEventListener("click", () => {
      const triageState = button.dataset.reviewBulkAssign;
      if (triageState) void submitReviewQueueBulkAssign(triageState, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-review-bulk-complete]").forEach((button) => {
    button.addEventListener("click", () => {
      const taskIds = (button.dataset.reviewBulkComplete || "").split(",").map((value) => value.trim()).filter(Boolean);
      if (taskIds.length > 0) void submitReviewQueueBulkComplete(taskIds, button.dataset.reviewBulkLane || "review", statusElement, actions);
    });
  });
}

export async function submitReviewQueueBulkAssign(
  triageState: string,
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  const defaultAssignee = getDashboardOperator() || "unassigned";
  const assignedTo = window.prompt(`Assign ${triageState} review lane to:`, defaultAssignee);
  if (!assignedTo?.trim()) {
    statusElement.textContent = "Review lane assignment cancelled; no assignee entered.";
    return;
  }
  statusElement.textContent = `Assigning ${triageState} review lane...`;
  try {
    const response = await authenticatedFetch("/dashboard/lead-factory/review-queue/assign", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        triageState,
        assignedTo: assignedTo.trim(),
        limit: 25,
        note: "Bulk assigned from Review Queue dashboard."
      })
    });
    const body = (await response.json()) as { error?: string; assignedCount?: number };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `Assigned ${formatInteger(body.assignedCount ?? 0)} ${triageState} review tasks to ${assignedTo.trim()}. Refreshing...`;
    window.setTimeout(() => actions.refresh(), 900);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Review lane assignment failed.";
  }
}

export async function submitReviewQueueBulkComplete(
  taskIds: string[],
  laneLabel: string,
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  const outcome = window.prompt(
    `Review outcome for ${taskIds.length} visible ${laneLabel} task(s):`,
    defaultReviewOutcomeForLane(laneLabel)
  );
  const normalizedOutcome = outcome?.trim() ?? "";
  if (!leadFactoryReviewOutcomes.includes(normalizedOutcome as (typeof leadFactoryReviewOutcomes)[number])) {
    statusElement.textContent = `Review completion cancelled; choose one of ${leadFactoryReviewOutcomes.join(", ")}.`;
    return;
  }
  const reason = window.prompt("Review reason:", normalizedOutcome) || normalizedOutcome;
  const confirmed = window.confirm(`Complete ${taskIds.length} visible ${laneLabel} review task(s) as ${normalizedOutcome}?`);
  if (!confirmed) {
    statusElement.textContent = "Review batch completion cancelled.";
    return;
  }
  statusElement.textContent = `Completing ${taskIds.length} ${laneLabel} review task(s) as ${normalizedOutcome}...`;
  try {
    const response = await authenticatedFetch("/dashboard/lead-factory/review-queue/complete", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        taskIds,
        outcome: normalizedOutcome,
        reason: reason.trim() || normalizedOutcome,
        note: `Batch completed from Review Queue dashboard: ${normalizedOutcome}.`
      })
    });
    const body = (await response.json()) as { error?: string; completedCount?: number; skippedCount?: number; outcome?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `Completed ${formatInteger(body.completedCount ?? 0)} review task(s) as ${body.outcome ?? normalizedOutcome}; skipped ${formatInteger(body.skippedCount ?? 0)}. Refreshing...`;
    window.setTimeout(() => actions.refresh(), 900);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Review batch completion failed.";
  }
}

export function defaultReviewOutcomeForLane(laneLabel: string): (typeof leadFactoryReviewOutcomes)[number] {
  if (laneLabel === "blocked") return "needs_rework";
  return "evidence_confirmed";
}
